/**
 * Audit Log Types
 *
 * Type definitions for the Admin Audit Log Viewer.
 * Based on audit_log.yaml schema and AuditEventType enum.
 */

// ===================================
// Core Audit Log Types
// ===================================

export interface AuditLog {
  uid: number
  userId: string
  orgId: string | null
  ipAddress: string | null
  source: string
  metadata: Record<string, any>
  eventType: string
  eventDetails: Record<string, any>
  version: number
  createdAt: string
  createdBy: string | null
  modifiedAt: string | null
  modifiedBy: string | null
  deletedAt: string | null
  deletedBy: string | null
}

// ===================================
// Filter Types
// ===================================

export interface AuditLogFilters {
  dateRange?: [string, string] | null
  eventTypes?: string[]
  userId?: string
  userName?: string
  orgId?: string
  orgName?: string
  source?: string
  ipAddress?: string
}

// ===================================
// API Request/Response Types
// ===================================

export interface AuditLogSearchParams {
  filters?: Array<{
    key: string
    constraint_type: string
    value?: any
    values?: any[]
  }>
  /** Sort specifications - snake_case for backend API */
  order_by?: Array<{ key: string; direction: 'asc' | 'desc' }>
  limit?: number
  offset?: number
  /** Include soft-deleted records */
  includeDeleted?: boolean
}

export interface AuditLogListResponse {
  data: {
    events: AuditLog[]
    totalCount: number
  }
  meta: {
    limit: number
    offset: number
    hasMore: boolean
  }
  errors?: Array<{
    code: string
    message: string
    details?: any
  }>
}

// ===================================
// Event Type Categories
// ===================================

export const EventTypeCategories = {
  Administrative: [
    'ADMIN_MEMBERSHIP_CREATED',
    'ADMIN_ORG_CREATED',
    'ADMIN_PROVISION_FAILED',
    'ADMIN_PROVISION_PARTIAL_FAILURE',
    'ADMIN_SUBSCRIPTION_CREATED',
    'ADMIN_USER_PROVISIONED',
    'SEED_OPERATION_COMPLETED',
    'SEED_OPERATION_FAILED',
    'SEED_OPERATION_STARTED',
  ],
  Authentication: [
    'AUTHENTICATION_FAILED',
    'LOGIN',
    'login_success',
    'LOGOUT',
    'SESSION_TIMEOUT',
  ],
  Authorization: [
    'ACCESS_DENIED',
    'AUTHORIZATION_CONTEXT_MISMATCH',
    'AUTHORIZATION_DENIED',
    'ORG_SWITCH',
    'PRIVILEGED_ACCESS',
    'PRIVILEGED_METHOD_ACCESS',
  ],
  Connections: [
    'CONNECTION_CREATED',
    'CONNECTION_DELETED',
    'CONNECTION_HEALTH_CHECK',
    'CONNECTION_RECONNECTED',
    'CONNECTION_TESTED',
    'CONNECTION_TEST_FAILED',
    'CONNECTION_UPDATED',
    'CREDENTIAL_ROTATED',
    'OAUTH_COMPLETED',
    'OAUTH_FAILED',
    'TOKEN_REFRESH_FAILED',
    'TOKEN_REFRESH_SUCCESS',
  ],
  'Data Operations': [
    'BULK_IMPORT',
    'COST_UPDATE',
    'EXPORT_GENERATED',
    'FIELD_MAPPING_CREATED',
    'FIELD_MAPPING_UPDATED',
    'PRESIGN_UPLOAD_GENERATED',
    'PRICE_UPDATE',
  ],
  'Domain & Signup': [
    'ATTRIBUTION_LINKED',
    'FEDERATED_ACCOUNT_LINKED',
    'SIGNUP_BLOCKED_BLACKLIST',
    'SIGNUP_QUARANTINED',
  ],
  System: [
    'AUDIT_LOG_DELETE',
    'AUDIT_LOG_QUERY_GLOBAL',
    'CLEANUP_JOB',
    'CONFIG_CHANGED',
    'HEALTH_CHECK',
    'MIGRATION_RUN',
  ],
  'Token Management': [
    'INVALID_TOKEN_DETECTED',
    'TOKEN_GENERATION_FAILED',
    'TOKEN_ISSUED',
    'TOKEN_ORG_ACCESS_DENIED',
    'TOKEN_REFRESH',
    'TOKEN_REFRESH_FAILED',
  ],
  'User Lifecycle': [
    'COGNITO_USER_CREATED',
    'COGNITO_USER_CREATE_FAILED',
    'COGNITO_USER_ROLLBACK_FAILED',
    'COGNITO_USER_ROLLBACK_STARTED',
    'COGNITO_USER_ROLLBACK_SUCCESS',
    'EMAIL_VERIFICATION_CONFIRMED',
    'EMAIL_VERIFICATION_SENT',
    'EULA_ACCEPTED',
    'PASSWORD_RESET_COMPLETED',
    'PASSWORD_RESET_REQUESTED',
    'USER_CREATED',
    'USER_CREATED_WITH_COGNITO_FAILED',
    'USER_CREATED_WITH_COGNITO_SUCCESS',
    'USER_PROFILE_UPDATED',
  ],
  Webhooks: [
    'WEBHOOK_DELIVERED',
    'WEBHOOK_FAILED',
    'WEBHOOK_PROCESSED',
    'WEBHOOK_RECEIVED',
    'WEBHOOK_RETRY',
  ],
} as const

export type EventTypeCategory = keyof typeof EventTypeCategories

// ===================================
// Source Options
// ===================================

export const SourceOptions = [
  'audit_log_service_sync',
  'auth_service',
  'connection_service',
  'data_service',
  'eula_accept',
  'system',
  'user_service',
  'webhook_processor',
] as const

export type AuditSource = (typeof SourceOptions)[number]

// ===================================
// UI State Types
// ===================================

export interface PaginationState {
  current: number
  pageSize: number
  total: number
}

export interface SortState {
  field: string
  direction: 'asc' | 'desc'
}

export interface ExportFormat {
  type: 'csv' | 'json' | 'ndjson'
  scope: 'currentPage' | 'allFiltered'
}

// ===================================
// Date Range Presets
// ===================================

export const DateRangePresets = [
  { label: 'Last 1 Hour', value: 1, unit: 'hours' as const },
  { label: 'Last 24 Hours', value: 24, unit: 'hours' as const },
  { label: 'Last 7 Days', value: 7, unit: 'days' as const },
  { label: 'Last 30 Days', value: 30, unit: 'days' as const },
  { label: 'Last 90 Days', value: 90, unit: 'days' as const },
] as const

// ===================================
// Status Indicator Types
// ===================================

export type EventStatus = 'success' | 'warning' | 'error'

export interface EventStatusIndicator {
  status: EventStatus
  icon: React.ReactNode
  color: string
}

// ===================================
// Enriched View Types (from vauditlog view)
// ===================================

export interface EnrichedAuditLog extends AuditLog {
  userName?: string
  userEmail?: string
  orgName?: string
}
